// ThemeApply hook. Two responsibilities, both fired by the same set of
// LiveView push_events from AppearanceLive:
//
//   1. Reflect onto <html>/<head> immediately — flip data-theme /
//      data-font on <html>, rewrite (or remove) the
//      <style id="theme-custom-palette"> block. The root layout's
//      data-theme + data-font attributes are HTTP-render-time values, so
//      without this in-page reskin a picker click would only show after
//      the next navigation.
//
//   2. Persist to ui_theme / ui_font / ui_palette cookies so a subsequent
//      *unauthenticated* page load (login, signup) can be painted in the
//      same palette before first paint. The read companion is
//      `assets/ts/theme-cookie-boot.js` (inlined into root.html.heex via
//      `OperationWeb.ThemeCookieBoot`). Cookies are written on apply_*
//      and CLEARED on clear_custom_palette so a logged-out user landing
//      on the login page sees the right theme even if they later cleared
//      their custom palette.
//
// The hook is attached to the body element (registered via phx-hook on
// the root layout) so it's mounted on every LiveView page, not just
// AppearanceLive — that's required because the user might pop open a
// second tab on a profile page and expect the broadcast from
// AppearanceLive on tab 1 to reskin tab 2 too.

import type { LiveViewHook, ThemePushEventWire } from "./types";
// Validators + types are paradox-generated from `theme.dox`
// (`type CustomPaletteEntry`, `type ApplyCustomPalettePayload`).
// The hand-rolled interfaces above the consumer have been dropped.
// applyTheme / applyFont take string payloads that can't be wrapped
// in a paradox `type` today (paradox requires >=2 fields per type),
// so they validate the inner field via `validTheme` / `validFont`
// directly at the push-event boundary.
import {
  validApplyCustomPalettePayload,
  validTheme,
  validFont,
  type ApplyCustomPalettePayload,
} from "../../../.dox/theme";

const CUSTOM_PALETTE_STYLE_ID = "theme-custom-palette";

// One year — long enough to outlive any plausible logout/relogin gap so a
// returning user lands on the login page already painted in their palette.
const COOKIE_MAX_AGE_SECONDS = 31_536_000;
const INTEGER_STRING_RE = /^-?\d+$/;

const writeCookie = (name: string, value: string): void => {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${COOKIE_MAX_AGE_SECONDS};SameSite=Lax`;
};

// Accept any string (the dox `validTheme` runs at the push-event
// boundary in ThemeApply.mounted below); the regex guard stays as
// a belt-and-braces defense for direct callers of applyTheme (the
// theme-cookie-boot script, the appearance-page preview pane).
export const applyTheme = (theme: string): void => {
  if (!/^[a-zA-Z0-9_-]+$/.test(theme)) {
    throw new Error(`apply_theme: invalid theme tag '${theme}'`);
  }
  document.documentElement.setAttribute("data-theme", theme);
  writeCookie("ui_theme", theme);
};

export const applyFont = (font: string): void => {
  if (!/^[a-zA-Z0-9_-]+$/.test(font)) {
    throw new Error(`apply_font: invalid font tag '${font}'`);
  }
  document.documentElement.setAttribute("data-font", font);
  writeCookie("ui_font", font);
};

// Delete a cookie by setting an expired Max-Age. Path/SameSite must
// match the writer for the browser to overwrite the original cookie.
const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;path=/;max-age=0;SameSite=Lax`;
};

export const clearCustomPalette = (): void => {
  const style = document.getElementById(CUSTOM_PALETTE_STYLE_ID);
  if (style) style.remove();
  deleteCookie("ui_palette");
};

export const applyCustomPalette = (
  payload: ApplyCustomPalettePayload,
): void => {
  const { selector, entries } = payload;
  if (!selector.startsWith(":root[data-theme=")) {
    throw new Error(`apply_custom_palette: refused selector '${selector}'`);
  }
  const lines = entries.map((e) => {
    if (!/^[a-z][a-z0-9-]*$/.test(e.cssVar)) {
      throw new Error(`apply_custom_palette: invalid cssVar '${e.cssVar}'`);
    }
    if (
      !Number.isInteger(e.r) ||
      !Number.isInteger(e.g) ||
      !Number.isInteger(e.b) ||
      e.r < 0 ||
      e.g < 0 ||
      e.b < 0 ||
      e.r > 255 ||
      e.g > 255 ||
      e.b > 255
    ) {
      throw new Error(
        `apply_custom_palette: invalid rgb (${e.r},${e.g},${e.b}) for ${e.cssVar}`,
      );
    }
    return `  --c-${e.cssVar}: ${e.r} ${e.g} ${e.b};`;
  });
  const css = `${selector} {\n${lines.join("\n")}\n}`;
  let style = document.getElementById(
    CUSTOM_PALETTE_STYLE_ID,
  ) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = CUSTOM_PALETTE_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = css;
  writeCookie("ui_palette", JSON.stringify(payload));
};

const normalizeRgbChannel = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!INTEGER_STRING_RE.test(trimmed)) return value;
  return Number.parseInt(trimmed, 10);
};

const normalizeCustomPaletteEntry = (entry: unknown): unknown => {
  if (entry === null || typeof entry !== "object" || Array.isArray(entry)) {
    return entry;
  }
  const record = entry as Record<string, unknown>;
  return {
    ...record,
    r: normalizeRgbChannel(record["r"]),
    g: normalizeRgbChannel(record["g"]),
    b: normalizeRgbChannel(record["b"]),
  };
};

const normalizeApplyCustomPalettePayload = (payload: unknown): unknown => {
  if (
    payload === null ||
    typeof payload !== "object" ||
    Array.isArray(payload)
  ) {
    return payload;
  }
  const record = payload as Record<string, unknown>;
  const entries = record["entries"];
  if (!Array.isArray(entries)) return payload;
  return {
    ...record,
    entries: entries.map((entry) => normalizeCustomPaletteEntry(entry)),
  };
};

export const ThemeApply = {
  mounted(this: LiveViewHook<ThemePushEventWire>) {
    this.handleEvent("applyTheme", (payload: unknown) => {
      // Single-field payload {theme: <Theme tag>} — validTheme
      // pins the inner string to one of the dox Theme union members.
      const { theme } = payload as { theme: unknown };
      applyTheme(validTheme(theme));
    });
    this.handleEvent("applyFont", (payload: unknown) => {
      const { font } = payload as { font: unknown };
      applyFont(validFont(font));
    });
    this.handleEvent("applyCustomPalette", (payload: unknown) => {
      const normalizedPayload = normalizeApplyCustomPalettePayload(payload);
      // validApplyCustomPalettePayload throws on missing/wrong-typed
      // selector / entries, and recursively runs validCustomPaletteEntry
      // for each entry — so the existing per-entry RGB range guard in
      // applyCustomPalette is now belt-and-braces.
      applyCustomPalette(validApplyCustomPalettePayload(normalizedPayload));
    });
    this.handleEvent("clearCustomPalette", () => {
      clearCustomPalette();
    });
  },
};
